import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { notify, confirmDialog } from './ui/notify';
import { apiCall } from './config';
import { formatDateForDisplay, formatDateForBackend, ttmmyyyyToISO } from './dateUtils';
import { useTranslation } from './i18n/LanguageContext';

// Partner-Bereich Komponente
function PartnerSection({ user, t, expandedDate, dateRange, startDate, endDate }) {
  const [partnerCode, setPartnerCode] = useState('');
  const [partners, setPartners] = useState([]);
  const [codeInput, setCodeInput] = useState('');
  // Standardmäßig AUFGEKLAPPT – eingeklappt war der Bereich zu unauffällig,
  // kaum jemand hat die Partner-Funktion gefunden.
  const [showPartner, setShowPartner] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [partnerAvail, setPartnerAvail] = useState({});
  const [copied, setCopied] = useState(false);
  const [visibility, setVisibility] = useState(false);
  const [shareNumber, setShareNumber] = useState(false); // WhatsApp-Nummer für Partner sichtbar
  const [partnerOverview, setPartnerOverview] = useState([]);

  const userId = user?.id;

  const loadPartnerCode = useCallback(() => {
    if (!userId) return;
    apiCall(`/users/${userId}/partner-code`)
      .then(data => setPartnerCode(data.partner_code || ''))
      .catch(() => {});
  }, [userId]);

  const loadPartners = useCallback(() => {
    if (!userId) return;
    apiCall(`/users/${userId}/partners`)
      .then(data => setPartners(data || []))
      .catch(() => {});
  }, [userId]);

  const loadVisibility = useCallback(() => {
    if (!userId) return;
    apiCall(`/users/${userId}/partner-visibility`)
      .then(data => setVisibility(!!data.partner_visibility))
      .catch(() => {});
  }, [userId]);

  const loadShareNumber = useCallback(() => {
    if (!userId) return;
    apiCall(`/users/${userId}/wa-share-number`)
      .then(data => setShareNumber(!!data.allowed))
      .catch(() => {});
  }, [userId]);

  const loadPartnerOverview = useCallback(() => {
    if (!userId || !startDate || !endDate) return;
    apiCall(`/users/${userId}/partner-availabilities?start_date=${startDate}&end_date=${endDate}`)
      .then(data => setPartnerOverview(data || []))
      .catch(() => setPartnerOverview([]));
  }, [userId, startDate, endDate]);

  useEffect(() => {
    loadPartnerCode();
    loadPartners();
    loadVisibility();
    loadShareNumber();
  }, [loadPartnerCode, loadPartners, loadVisibility, loadShareNumber]);

  useEffect(() => {
    if (showPartner && partners.length > 0) {
      loadPartnerOverview();
    }
  }, [showPartner, partners, loadPartnerOverview]);

  // Load partner availability when date changes
  useEffect(() => {
    if (!expandedDate || partners.length === 0) { setPartnerAvail({}); return; }
    const avails = {};
    Promise.all(partners.map(p =>
      apiCall(`/users/${userId}/partner-availability/${p.id}/${expandedDate}`)
        .then(data => { avails[p.id] = data; })
        .catch(() => { avails[p.id] = null; })
    )).then(() => setPartnerAvail({...avails}));
  }, [expandedDate, partners, userId]);

  const showMsg = (msg, type = 'success') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleToggleVisibility = (checked) => {
    setVisibility(checked);
    apiCall(`/users/${userId}/partner-visibility`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ partner_visibility: checked })
    }).then(() => {
      showMsg(checked ? t('partner.visibilityOn') : t('partner.visibilityOff'));
    }).catch(() => {
      setVisibility(!checked);
      showMsg(t('common.error'), 'danger');
    });
  };

  const handleToggleShareNumber = (checked) => {
    setShareNumber(checked);
    apiCall(`/users/${userId}/wa-share-number`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ allowed: checked })
    }).then(() => {
      showMsg(t('common.saved') !== 'common.saved' ? t('common.saved') : 'Gespeichert ✅');
    }).catch(() => {
      setShareNumber(!checked);
      showMsg(t('common.error'), 'danger');
    });
  };

  const handleLinkPartner = () => {
    if (!codeInput.trim()) return;
    apiCall('/partner-link', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, partner_code: codeInput.trim() })
    })
      .then(data => {
        if (data.error) { showMsg(data.error, 'danger'); return; }
        showMsg(t('partner.linked'));
        setCodeInput('');
        loadPartners();
      })
      .catch(err => showMsg(err.message || t('common.error'), 'danger'));
  };

  const handleUnlink = (partnerId) => {
    apiCall(`/users/${userId}/partner-preferences/${partnerId}`, { method: 'DELETE' })
      .then(() => {
        apiCall(`/users/${partnerId}/partner-preferences/${userId}`, { method: 'DELETE' }).catch(() => {});
        showMsg(t('partner.unlinked'));
        loadPartners();
      })
      .catch(err => showMsg(err.message || t('common.error'), 'danger'));
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(partnerCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleRegenerate = () => {
    apiCall(`/users/${userId}/partner-code/regenerate`, { method: 'POST' })
      .then(data => {
        setPartnerCode(data.partner_code);
        showMsg(t('partner.codeRegenerated'));
      })
      .catch(() => showMsg(t('common.error'), 'danger'));
  };

  return (
    <div className="card mb-4">
      <div 
        className="card-header d-flex justify-content-between align-items-center" 
        style={{ cursor: 'pointer' }}
        onClick={() => setShowPartner(!showPartner)}
      >
        <span><i className="bi bi-people-fill me-2"></i>{t('partner.title')}</span>
        <span className="d-flex align-items-center">
          {partners.length > 0 && (
            <span className="badge bg-primary me-2">{partners.length}</span>
          )}
          <i className={`bi bi-chevron-${showPartner ? 'up' : 'down'}`}></i>
        </span>
      </div>
      {showPartner && (
        <div className="card-body">
          {message && (
            <div className={`alert alert-${messageType} py-2 small`}>{message}</div>
          )}

          {/* Sichtbarkeit Toggle */}
          <div 
            className={`d-flex align-items-center gap-3 mb-3 p-3 rounded border ${visibility ? 'border-success bg-success bg-opacity-10' : 'border-secondary bg-light'}`}
            style={{ cursor: 'pointer' }}
            onClick={() => handleToggleVisibility(!visibility)}
          >
            <div className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${visibility ? 'bg-success' : 'bg-secondary'}`} 
                 style={{ width: '36px', height: '36px', transition: 'background-color 0.3s' }}>
              <i className={`bi ${visibility ? 'bi-eye-fill' : 'bi-eye-slash'} text-white`}></i>
            </div>
            <div className="flex-grow-1">
              <div className={`fw-bold small ${visibility ? 'text-success' : 'text-secondary'}`}>
                {t('partner.visibilityLabel')}
              </div>
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                {t('partner.visibilityHint')}
              </div>
            </div>
            <div className="form-check form-switch mb-0" onClick={e => e.stopPropagation()}>
              <input
                className="form-check-input"
                type="checkbox"
                id="partnerVisibility"
                checked={visibility}
                onChange={e => handleToggleVisibility(e.target.checked)}
                style={{ width: '3em', height: '1.5em', cursor: 'pointer' }}
              />
            </div>
          </div>

          {/* Handynummer für Partner sichtbar (WhatsApp) */}
          <div
            className={`d-flex align-items-center gap-3 mb-3 p-3 rounded border ${shareNumber ? 'border-success bg-success bg-opacity-10' : 'border-secondary bg-light'}`}
            style={{ cursor: 'pointer' }}
            onClick={() => handleToggleShareNumber(!shareNumber)}
          >
            <div className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${shareNumber ? 'bg-success' : 'bg-secondary'}`}
                 style={{ width: '36px', height: '36px', transition: 'background-color 0.3s' }}>
              <i className={`bi ${shareNumber ? 'bi-telephone-fill' : 'bi-telephone-x'} text-white`}></i>
            </div>
            <div className="flex-grow-1">
              <div className={`fw-bold small ${shareNumber ? 'text-success' : 'text-secondary'}`}>
                {t('partner.shareNumberLabel')}
              </div>
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                {t('partner.shareNumberHint')}
              </div>
            </div>
            <div className="form-check form-switch mb-0" onClick={e => e.stopPropagation()}>
              <input
                className="form-check-input"
                type="checkbox"
                id="partnerShareNumber"
                checked={shareNumber}
                onChange={e => handleToggleShareNumber(e.target.checked)}
                style={{ width: '3em', height: '1.5em', cursor: 'pointer' }}
              />
            </div>
          </div>

          {/* Mein Partnercode */}
          <div className="mb-3">
            <label className="form-label small text-muted fw-bold">
              <i className="bi bi-qr-code me-1"></i>{t('partner.myCode')}
            </label>
            <div className="d-flex align-items-center gap-2">
              <code className="fs-5 bg-light px-3 py-2 rounded border user-select-all">
                {partnerCode || '...'}
              </code>
              <button className="btn btn-sm btn-outline-primary" onClick={handleCopy} title={t('partner.copy')}>
                <i className={`bi ${copied ? 'bi-check-lg text-success' : 'bi-clipboard'}`}></i>
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={handleRegenerate} title={t('partner.regenerate')}>
                <i className="bi bi-arrow-clockwise"></i>
              </button>
            </div>
            <small className="text-muted">{t('partner.shareHint')}</small>
          </div>

          {/* Partner hinzufügen */}
          <div className="mb-3">
            <label className="form-label small text-muted fw-bold">
              <i className="bi bi-person-plus me-1"></i>{t('partner.addByCode')}
            </label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="z.B. K7X-4M2"
                value={codeInput}
                onChange={e => setCodeInput(e.target.value.toUpperCase())}
                maxLength={7}
                onKeyDown={e => e.key === 'Enter' && handleLinkPartner()}
              />
              <button className="btn btn-primary" onClick={handleLinkPartner} disabled={!codeInput.trim()}>
                <i className="bi bi-link-45deg me-1"></i>{t('partner.link')}
              </button>
            </div>
          </div>

          {/* Partner-Liste */}
          {partners.length > 0 ? (
            <div>
              <label className="form-label small text-muted fw-bold">
                <i className="bi bi-people me-1"></i>{t('partner.myPartners')}
              </label>
              {partners.map(p => (
                <div key={p.id} className="d-flex align-items-center justify-content-between bg-light rounded p-2 mb-2">
                  <div>
                    <span className="fw-bold">{p.firstname} {p.lastname}</span>
                    {expandedDate && partnerAvail[p.id] !== undefined && (
                      <div className="small mt-1">
                        {partnerAvail[p.id] === null ? (
                          <span className="text-muted">{t('partner.noAccess')}</span>
                        ) : partnerAvail[p.id].length === 0 ? (
                          <span className="text-muted">
                            <i className="bi bi-dash-circle me-1"></i>{t('partner.noEntry')}
                          </span>
                        ) : partnerAvail[p.id].map((a, i) => (
                          <span key={i} className={`badge me-1 ${a.is_available ? 'bg-success' : 'bg-danger'}`}>
                            {a.is_available ? `${a.start_time}–${a.end_time}` : t('avail.notAvailable')}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => handleUnlink(p.id)}>
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted small mb-0">
              <i className="bi bi-info-circle me-1"></i>{t('partner.noPartners')}
            </p>
          )}

          {/* Partner-Übersicht: alle Tage */}
          {partnerOverview.length > 0 && dateRange.length > 0 && (
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">
                  <i className="bi bi-calendar-week me-2"></i>{t('partner.overview')}
                </h6>
                <button className="btn btn-sm btn-outline-secondary" onClick={loadPartnerOverview} title={t('partner.refresh')}>
                  <i className="bi bi-arrow-clockwise"></i>
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-sm table-bordered align-middle" style={{ fontSize: '0.8rem' }}>
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: '90px' }}>{t('avail.weekday')}</th>
                      {partnerOverview.map(p => (
                        <th key={p.id} className="text-center">{p.firstname}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dateRange.map(dateStr => {
                      const dateObj = new Date(dateStr + 'T00:00:00');
                      const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
                      return (
                        <tr key={dateStr} className={isWeekend ? 'table-secondary' : ''}>
                          <td className="text-nowrap">
                            <strong>{dateObj.toLocaleDateString('de-DE', { weekday: 'short' })}</strong>{' '}
                            {dateObj.getDate()}.{dateObj.getMonth() + 1}.
                          </td>
                          {partnerOverview.map(p => {
                            const dayAvails = p.availabilities.filter(a => a.date === dateStr);
                            return (
                              <td key={p.id} className="text-center">
                                {dayAvails.length === 0 ? (
                                  <span className="text-muted">–</span>
                                ) : dayAvails.map((a, i) => (
                                  <div key={i}>
                                    {a.is_available ? (
                                      <span className="badge bg-success" style={{ fontSize: '0.7rem' }}>
                                        {a.start_time}–{a.end_time}
                                      </span>
                                    ) : (
                                      <span className="badge bg-danger" style={{ fontSize: '0.7rem' }}>✕</span>
                                    )}
                                  </div>
                                ))}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <small className="text-muted">
                <i className="bi bi-info-circle me-1"></i>{t('partner.overviewHint')}
              </small>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function UserAvailability({ user }) {
  const [availabilities, setAvailabilities] = useState([]);
  const [users, setUsers] = useState([]);
  const [userStatuses, setUserStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAvailability, setEditingAvailability] = useState(null);
  // Standard: neuer Eintrag ERSETZT die bestehenden des Tages. Mitarbeiter
  // "aktualisieren" sonst mehrfach und stapeln Einträge übereinander –
  // getrennte Zeitfenster (vormittags + abends) gehen bewusst per Auswahl.
  const [replaceExisting, setReplaceExisting] = useState(true);
  const [filterUser, setFilterUser] = useState('');
  const [searchAll, setSearchAll] = useState('');
  const [searchNoAvail, setSearchNoAvail] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isMultipleDays, setIsMultipleDays] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  
  // Neue State-Variablen für Kalender-Ansicht
  const [startDate, setStartDate] = useState('2026-09-19'); // Oktoberfest 2026
  const [endDate, setEndDate] = useState('2026-10-04'); // Oktoberfest 2026
  const [selectedDate, setSelectedDate] = useState('');
  const [dateRange, setDateRange] = useState([]);
  const [expandedDate, setExpandedDate] = useState(null);
  // Gesperrte Tage: bereits im Dienstplan eingeteilt → Verfügbarkeit nur noch über die Leitung änderbar
  const [lockedDates, setLockedDates] = useState(new Set());
  const [lockedMsg, setLockedMsg] = useState('');
  useEffect(() => {
    const uid = user.role === 'employee' ? user.id : null; // Admin/Leitung/Planer sind nicht gesperrt
    if (!uid) { setLockedDates(new Set()); return; }
    apiCall(`/user-availability/locked/${uid}?year=${new Date().getFullYear()}`)
      .then((r) => { setLockedDates(new Set((r && r.dates) || [])); setLockedMsg((r && r.message) || ''); })
      .catch(() => setLockedDates(new Set()));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const isLocked = (d) => lockedDates.has(String(d || '').slice(0, 10));

  // Bedarfs-Ampel je Tag (wie viel ist noch offen?) – Lücken <= 3 h werden ignoriert
  const [demand, setDemand] = useState({});
  useEffect(() => {
    apiCall(`/availability-demand?year=${new Date().getFullYear()}`)
      .then((d) => setDemand(d && typeof d === 'object' ? d : {}))
      .catch(() => setDemand({}));
  }, []);
  const fmtH = (h) => String(h).padStart(2, '0') + ':00';
  const demandText = (dateStr) => {
    const d = demand[dateStr];
    if (!d) return null;
    if (d.level === 'full') {
      return { icon: '✅', color: 'text-muted', short: 'voll besetzt',
        long: `Dieser Tag ist aktuell **voll besetzt** (${d.pct}% geplant).` +
          (d.reserve ? ` ${d.reserve} weitere haben sich schon als Reserve eingetragen.` : '') +
          ' Trag dich trotzdem gern ein – es fällt immer wieder jemand aus.' };
    }
    const win = d.openBlocks.map((b) => `${fmtH(b.from)}–${fmtH(b.to)}`).join(' und ');
    const many = d.level === 'high';
    return {
      icon: many ? '🔥' : '🟡', color: many ? 'text-danger fw-bold' : 'text-warning',
      short: many ? 'dringend gesucht' : 'einzelne Schichten frei',
      long: `${many ? '**Hier suchen wir dringend Verstärkung!**' : 'Hier sind noch Schichten frei.'} Offen: ${win}` +
        (d.openBlocks[0] && d.openBlocks[0].peak > 1 ? ` (bis zu ${Math.max(...d.openBlocks.map((b) => b.peak))} Personen gleichzeitig)` : '') + '.',
    };
  };
  // Beim Öffnen eines (anderen) Tages immer auf "Ersetzen" zurücksetzen
  useEffect(() => { setReplaceExisting(true); }, [expandedDate]);
  const [sliderStart, setSliderStart] = useState(4);  // 07:00
  const [sliderEnd, setSliderEnd] = useState(40);     // 01:00
  const [applyDates, setApplyDates] = useState([]);

  const { t } = useTranslation();

  const dayNames = [t('day.sunday'), t('day.monday'), t('day.tuesday'), t('day.wednesday'), t('day.thursday'), t('day.friday'), t('day.saturday')];

  // Time slots: 05:00 bis 01:00 (nächster Tag) in 30-Min-Schritten.
  // Ab 05:00 wegen Frühöffnung Wiesn-Garten (Sa/Feiertag); Standard-Auswahl
  // bleibt 07:00 (Index 4).
  const TIME_SLOTS = useMemo(() => {
    const slots = [];
    for (let i = 0; i <= 40; i++) {
      const totalMinutes = 5 * 60 + i * 30;
      const hours = Math.floor(totalMinutes / 60) % 24;
      const minutes = totalMinutes % 60;
      slots.push(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`);
    }
    return slots;
  }, []);

  const timeToSliderIndex = (timeStr) => {
    if (!timeStr) return 4; // 07:00
    const [h, m] = timeStr.split(':').map(Number);
    let totalMinutes = h * 60 + m;
    if (h < 5) totalMinutes += 24 * 60; // 00:00–02:00 = Folgetag
    return Math.max(0, Math.min(40, Math.round((totalMinutes - 5 * 60) / 30)));
  };

  // Utility-Funktion für Erfolgs-Nachricht
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000); // Nach 3 Sekunden verschwinden
  };

  const [formData, setFormData] = useState({
    user_id: '',
    date: '', // Statt day_of_week
    start_time: '',
    end_time: '',
    is_available: true,
    notes: '',
    availability_type: 'custom' // custom, flexible, morning, afternoon, evening
  });

  useEffect(() => {
    // Bei Mitarbeitern automatisch den eigenen User vorauswählen
    if (user && user.role === 'employee' && user.id) {
      setFilterUser(user.id.toString());
    }
    
    loadUsers();
    
    // Immer zuerst die aktuellen Vertragsdaten laden
    apiCall('/contract-availability-dates')
      .then(data => {
        console.log('Empfangene Vertragsdaten:', data);
        if (data.start_date && data.end_date) {
          // Backend sendet jetzt TTMMYYYY Format - zu ISO konvertieren für date inputs
          const isoStartDate = ttmmyyyyToISO(data.start_date);
          const isoEndDate = ttmmyyyyToISO(data.end_date);
          
          console.log('Setze Datumsbereich:', isoStartDate, 'bis', isoEndDate);
          
          setStartDate(isoStartDate);
          setEndDate(isoEndDate);
          generateDateRange(isoStartDate, isoEndDate);
          
          // Lösche localStorage-Daten, damit die neuen Vertragsdaten verwendet werden
          localStorage.removeItem('availability_start_date');
          localStorage.removeItem('availability_end_date');
        } else {
          // Fallback: Gespeicherte Datums-Einstellungen aus localStorage laden
          const savedStartDate = localStorage.getItem('availability_start_date');
          const savedEndDate = localStorage.getItem('availability_end_date');
          
          if (savedStartDate && savedEndDate) {
            setStartDate(savedStartDate);
            setEndDate(savedEndDate);
            generateDateRange(savedStartDate, savedEndDate);
          } else {
            // Letzter Fallback: Initialer Zeitraum (nächste 2 Wochen)
            const today = new Date();
            const start = today.toISOString().split('T')[0];
            const end = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            setStartDate(start);
            setEndDate(end);
            generateDateRange(start, end);
          }
        }
      })
      .catch(error => {
        console.error('Fehler beim Laden der Vertragsdaten:', error);
        // Fallback: Gespeicherte Datums-Einstellungen aus localStorage laden
        const savedStartDate = localStorage.getItem('availability_start_date');
        const savedEndDate = localStorage.getItem('availability_end_date');
        
        if (savedStartDate && savedEndDate) {
          setStartDate(savedStartDate);
          setEndDate(savedEndDate);
          generateDateRange(savedStartDate, savedEndDate);
        } else {
          // Letzter Fallback: Initialer Zeitraum (nächste 2 Wochen)
          const today = new Date();
          const start = today.toISOString().split('T')[0];
          const end = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          setStartDate(start);
          setEndDate(end);
          generateDateRange(start, end);
        }
      });
    
    // Gespeichertes ausgewähltes Datum laden
    const savedSelectedDate = localStorage.getItem('availability_selected_date');
    if (savedSelectedDate) {
      setSelectedDate(savedSelectedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (startDate && endDate) {
      loadAvailabilities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUser, startDate, endDate]);

  // Datum-Range generieren und speichern
  const generateDateRange = (start, end) => {
    const dates = [];
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d).toISOString().split('T')[0]);
    }
    setDateRange(dates);
    
    // Datums-Einstellungen in localStorage speichern
    localStorage.setItem('availability_start_date', start);
    localStorage.setItem('availability_end_date', end);
  };

  // Speichere ausgewähltes Datum
  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    localStorage.setItem('availability_selected_date', newDate);
  };

  // Speichere Zeitraum-Änderungen
  const handleDateRangeChange = (newStart, newEnd) => {
    setStartDate(newStart);
    setEndDate(newEnd);
    generateDateRange(newStart, newEnd);
  };

  // Kalendar-Daten als Date-Objekte
  const calendarDates = dateRange.map(dateStr => new Date(dateStr + 'T00:00:00'));

  useEffect(() => {
    const initializeData = async () => {
      await loadUsers();
      // Nur loadAvailabilities aufrufen, wenn startDate und endDate gesetzt sind
      if (startDate && endDate) {
        await loadAvailabilities();
      }
    };
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      loadAvailabilities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUser, startDate, endDate]);

  const loadUsers = async () => {
    try {
      const data = await apiCall('/users');
      console.log('All users from API:', data);
      
      // Mitarbeiter (role_id = 2), nicht abgesagt. (Gleiche Basis wie die
      // Verfügbarkeitsdaten — current_year_active NICHT erzwingen, sonst wäre
      // die Liste leer, wenn das Flag für die Saison nicht gesetzt ist.)
      let activeEmployees = data.filter(u => u.role_id === 2 && u.status !== 'abgesagt');

      // SICHERHEIT: Für normale Mitarbeiter nur den eigenen User anzeigen
      if (user && user.role === 'employee') {
        activeEmployees = activeEmployees.filter(emp => emp.id === user.id);
        console.log('Employee user - restricted to own user only:', activeEmployees);
      }
      
      console.log('Active employees after filtering:', activeEmployees);
      
      // Einfache Filterprüfung basierend auf user.status
      if (filterUser) {
        const currentUser = activeEmployees.find(u => u.id === parseInt(filterUser));
        if (!currentUser) {
          setFilterUser(''); // Filter zurücksetzen, da User nicht in aktiven Mitarbeitern
          console.log('Filter zurückgesetzt: Ausgewählter Mitarbeiter ist nicht verfügbar');
        }
      }
      
      setUsers(activeEmployees);
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer:', error);
    }
  };

  const loadAvailabilities = async () => {
    console.log('=== LOADING AVAILABILITIES ===');
    console.log('Start Date:', startDate);
    console.log('End Date:', endDate);
    console.log('Filter User:', filterUser);
    
    setLoading(true);
    try {
      // Lade alle aktiven Mitarbeiter
      const allUsers = await apiCall('/users');
      console.log('Loaded users:', allUsers.length);
      
      // Filtere nur aktive Mitarbeiter basierend auf role_id und status
      let activeUsers = allUsers.filter(user => {
        // Alle ARBEITENDEN Rollen: Mitarbeiter (2), Einsatzleitung (3) und
        // Stations-Planer (4, z.B. Jenny) – nur reine Admin-Konten (1) nicht.
        // Vorher fiel z.B. Jenny mit dem Wechsel auf Stations-Planer aus der Liste.
        const isEmployee = [2, 3, 4].includes(user.role_id);
        // Zeige alle Mitarbeiter außer denen, die explizit abgesagt haben
        const hasValidStatus = user.status !== "abgesagt";

        return isEmployee && hasValidStatus;
      });
      
      // SICHERHEIT: Für normale Mitarbeiter nur den eigenen User anzeigen
      if (user && user.role === 'employee') {
        activeUsers = activeUsers.filter(emp => emp.id === user.id);
        console.log('Employee user - availability restricted to own user only:', activeUsers);
      }
      
      console.log('Active users:', activeUsers.length);
      
      // Lade Verfügbarkeitseinträge
      let availabilityUrl = `/user-availability?startDate=${startDate}&endDate=${endDate}`;
      if (filterUser) {
        availabilityUrl += `&userId=${filterUser}`;
      }
      // SICHERHEIT: Für normale Mitarbeiter automatisch nur eigene Daten laden
      else if (user && user.role === 'employee') {
        availabilityUrl += `&userId=${user.id}`;
      }
      console.log('Making API call to:', availabilityUrl);
      const availabilityData = await apiCall(availabilityUrl);
      console.log('Loaded availability data:', availabilityData);
      
      // Erstelle kombinierte Daten: alle aktiven Mitarbeiter mit ihren Verfügbarkeiten (falls vorhanden)
      const combinedData = [];
      
      activeUsers.forEach(user => {
        // Finde Verfügbarkeitseinträge für diesen Mitarbeiter
        const userAvailabilities = availabilityData.filter(avail => avail.user_id === user.id);
        
        if (userAvailabilities.length > 0) {
          // Mitarbeiter hat Verfügbarkeitseinträge
          userAvailabilities.forEach(avail => {
            combinedData.push({
              ...avail,
              firstname: user.firstname,
              lastname: user.lastname
            });
          });
        } else {
          // Mitarbeiter hat keine Verfügbarkeitseinträge - erstelle leeren Eintrag
          combinedData.push({
            id: null,
            user_id: user.id,
            firstname: user.firstname,
            lastname: user.lastname,
            date: null,
            start_time: null,
            end_time: null,
            availability_type: null,
            notes: null
          });
        }
      });
      
      console.log('Kombinierte Verfügbarkeitsdaten:', combinedData);
      setAvailabilities(combinedData);
    } catch (error) {
      console.error('Fehler beim Laden der Verfügbarkeiten:', error);
    }
    setLoading(false);
  };

  const handleInlineSubmit = async () => {
    if (!expandedDate) return;
    
    const userId = user.role === 'employee' ? user.id : (filterUser ? parseInt(filterUser) : null);
    if (!userId) {
      notify(t('avail.filterFirst'));
      return;
    }
    
    // Sperre: bereits geplante Tage können nicht mehr selbst geändert werden
    const lockedHit = [expandedDate, ...(selectedDates || [])].filter((d) => d && isLocked(d));
    if (lockedHit.length) {
      notify(`🔒 ${lockedMsg || 'Für diesen Tag bist du bereits eingeplant – Änderung nur über die Leitung.'}`);
      return;
    }

    const startTime = TIME_SLOTS[sliderStart];
    const endTime = TIME_SLOTS[sliderEnd];
    const isFlexible = (startTime === '07:00' || startTime === '05:00') && endTime === '01:00';
    
    const baseData = {
      user_id: userId,
      start_time: formData.is_available ? startTime : '00:00',
      end_time: formData.is_available ? endTime : '00:00',
      is_available: formData.is_available,
      notes: formData.notes || (formData.is_available && isFlexible ? 'Flexibel verfügbar (ganzer Tag)' : ''),
      availability_type: formData.is_available ? (isFlexible ? 'flexible' : 'custom') : 'custom'
    };
    
    try {
      // Ersetzen-Modus: bestehende Einträge des Tages vorher entfernen, damit
      // "Aktualisieren" nicht stapelt (Doppel- und Überlapp-Einträge waren die
      // häufigste Fehlbedienung). "Zusätzliches Zeitfenster" lässt sie stehen.
      const deleteExistingFor = async (date) => {
        const olds = availabilities.filter((a) => a.date === date && a.user_id === userId && a.id != null);
        for (const o of olds) {
          try {
            await apiCall(`/user-availability/${o.id}`, { method: 'DELETE' });
          } catch (err) {
            console.error(`Alten Eintrag ${o.id} löschen fehlgeschlagen:`, err);
          }
        }
      };

      if (editingAvailability) {
        await apiCall(`/user-availability/${editingAvailability.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...baseData, date: expandedDate })
        });
      } else {
        if (replaceExisting) await deleteExistingFor(expandedDate);
        await apiCall('/user-availability', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...baseData, date: expandedDate })
        });
      }

      // Save for additional dates
      for (const date of applyDates) {
        try {
          if (!editingAvailability && replaceExisting) await deleteExistingFor(date);
          await apiCall('/user-availability', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...baseData, date })
          });
        } catch (err) {
          console.error(`Fehler bei Datum ${date}:`, err);
        }
      }
      
      const totalSaved = 1 + applyDates.length;
      await loadAvailabilities();
      setExpandedDate(null);
      setApplyDates([]);
      setEditingAvailability(null);
      showSuccess(totalSaved > 1 
        ? t('avail.savedMulti', { count: totalSaved }) 
        : t('avail.saved'));
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      notify('Fehler: ' + error.message);
    }
  };

  const handleEdit = (availability) => {
    setEditingAvailability(availability);
    setExpandedDate(availability.date);
    setSliderStart(timeToSliderIndex(availability.start_time));
    setSliderEnd(timeToSliderIndex(availability.end_time));
    setFormData({
      user_id: availability.user_id,
      date: availability.date,
      start_time: availability.start_time,
      end_time: availability.end_time,
      is_available: availability.is_available === 1 || availability.is_available === true,
      notes: availability.notes || '',
      availability_type: 'custom'
    });
    setApplyDates([]);
    setTimeout(() => {
      document.querySelector('.calendar-grid')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleDelete = async (id) => {
    if (await confirmDialog(t('avail.confirmDelete'))) {
      try {
        console.log('Deleting availability with ID:', id);
        const response = await apiCall(`/user-availability/${id}`, { method: 'DELETE' });
        console.log('Delete response:', response);
        
        // Nach erfolgreichem Löschen die Verfügbarkeiten neu laden
        console.log('Reloading availabilities after delete...');
        await loadAvailabilities();
        console.log('Availabilities reloaded successfully');
        
        // Erfolgs-Nachricht anzeigen
        showSuccess(t('avail.deleted'));
      } catch (error) {
        console.error('Fehler beim Löschen:', error);
        notify('Fehler beim Löschen der Verfügbarkeit: ' + error.message);
      }
    }
  };

  const resetForm = () => {
    const savedDate = localStorage.getItem('availability_selected_date') || selectedDate || '';
    
    let defaultUserId = filterUser || '';
    if (user && user.role === 'employee') {
      defaultUserId = user.id.toString();
    }
    
    setFormData({
      user_id: defaultUserId,
      date: savedDate,
      start_time: '07:00',
      end_time: '01:00',
      is_available: true,
      notes: '',
      availability_type: 'flexible'
    });
    setEditingAvailability(null);
    setSliderStart(4);  // 07:00
    setSliderEnd(40);   // 01:00
    setApplyDates([]);
  };

  // Verfügbarkeitstyp-Handler
  const handleAvailabilityTypeChange = (type) => {
    let newFormData = { ...formData, availability_type: type };
    
    switch (type) {
      case 'flexible':
        newFormData.start_time = '07:00';
        newFormData.end_time = '01:00'; // Bis 01:00 (nächster Tag)
        newFormData.notes = 'Flexibel verfügbar (ganzer Tag)';
        break;
      case 'morning':
        newFormData.start_time = '07:00';
        newFormData.end_time = '14:00';
        newFormData.notes = 'Flexibel verfügbar (Morgens)';
        break;
      case 'afternoon':
        newFormData.start_time = '11:00';
        newFormData.end_time = '18:00';
        newFormData.notes = 'Flexibel verfügbar (Mittags)';
        break;
      case 'evening':
        newFormData.start_time = '18:00';
        newFormData.end_time = '01:00'; // Bis 01:00 (nächster Tag)
        newFormData.notes = 'Flexibel verfügbar (Abends)';
        break;
      case 'custom':
        // Benutzer gibt eigene Zeiten ein
        newFormData.notes = '';
        break;
      default:
        break;
    }
    
    setFormData(newFormData);
  };

  // Verfügbarkeitstyp erkennen und Icon/Badge zurückgeben
  const getAvailabilityTypeDisplay = (availability) => {
    const startTime = availability.start_time;
    const endTime = availability.end_time;
    const notes = availability.notes || '';
    
    if (startTime === '07:00' && endTime === '01:00' && notes.includes('Flexibel verfügbar (ganzer Tag)')) {
      return <span className="badge bg-success me-1"><i className="bi bi-clock-history me-1"></i>{t('avail.flexible')}</span>;
    } else if (startTime === '07:00' && endTime === '14:00' && notes.includes('Flexibel verfügbar (Morgens)')) {
      return <span className="badge bg-warning me-1"><i className="bi bi-sunrise me-1"></i>{t('avail.morning')}</span>;
    } else if (startTime === '11:00' && endTime === '18:00' && notes.includes('Flexibel verfügbar (Mittags)')) {
      return <span className="badge bg-info me-1"><i className="bi bi-sun me-1"></i>{t('avail.midday')}</span>;
    } else if (startTime === '18:00' && endTime === '01:00' && notes.includes('Flexibel verfügbar (Abends)')) {
      return <span className="badge bg-primary me-1"><i className="bi bi-moon me-1"></i>{t('avail.evening')}</span>;
    }
    return <span className="badge bg-secondary me-1"><i className="bi bi-pencil me-1"></i>{t('avail.custom')}</span>;
  };

  const getUserStatusColor = (userId) => {
    const user = users.find(u => u.id === userId);
    const status = userStatuses[userId];
    if (!user || !status) return 'secondary';
    
    if (user.documents_complete) return 'success';
    if (status.auto_complete) return 'warning';
    return 'danger';
  };

  // Eindeutige Benutzer aus Verfügbarkeitsdaten extrahieren für Filter
  const getUniqueUsersFromAvailabilities = () => {
    const userMap = new Map();
    availabilities.forEach(availability => {
      if (!userMap.has(availability.user_id)) {
        const userName = (availability.firstname && availability.lastname) 
          ? `${availability.firstname} ${availability.lastname}`.trim()
          : availability.firstname || availability.lastname || `User ${availability.user_id}`;
        userMap.set(availability.user_id, {
          id: availability.user_id,
          name: userName
        });
      }
    });
    return Array.from(userMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  };

  const uniqueUsers = getUniqueUsersFromAvailabilities();

  // Verfügbarkeiten nach Benutzer gruppieren
  const groupedAvailabilities = availabilities.reduce((groups, availability) => {
    // Namen direkt aus den Verfügbarkeitsdaten verwenden (da sie jetzt von der API gejoined werden)
    const userName = (availability.firstname && availability.lastname) 
      ? `${availability.firstname} ${availability.lastname}`.trim()
      : availability.firstname || availability.lastname || `User ${availability.user_id}`;
    
    // Wenn ein spezifischer Benutzer gefiltert ist, nur dessen Verfügbarkeiten anzeigen
    if (filterUser && availability.user_id !== parseInt(filterUser)) {
      return groups;
    }
    
    if (!groups[userName]) {
      groups[userName] = [];
    }
    groups[userName].push(availability);
    return groups;
  }, {});

  // Mitarbeiter ohne (echte) Verfügbarkeit im aktuellen Zeitraum
  const usersWithoutAvailability = users.filter(u =>
    !availabilities.some(a => a.user_id === u.id && a.id != null)
  );

  return (
    <div className="container-fluid">
      {/* Erfolgs-Benachrichtigung */}
      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show position-fixed" 
             style={{ top: '20px', right: '20px', zIndex: 1050, maxWidth: '400px' }}>
          <i className="bi bi-check-circle-fill me-2"></i>
          {successMessage}
        </div>
      )}
      
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2><i className="bi bi-calendar-check me-2"></i>{t('avail.title')}</h2>
          </div>

          {/* Filter – nur für Admins. Mitarbeiter sahen hier nur eine Karte mit dem
              eigenen Namen („Sie können nur Ihre eigenen…") – überflüssig, daher weg. */}
          {user.role === 'admin' && (
          <div className="row mb-4">
            <div className="col-md-6 mb-3 mb-md-0">
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="card-title d-flex align-items-center justify-content-between">
                    <span>{t('avail.filter')}</span>
                    {filterUser && (
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setFilterUser(''); setSearchAll(''); }}>
                        <i className="bi bi-x-lg"></i> {t('avail.allEmployees')}
                      </button>
                    )}
                  </h6>
                  {user.role === 'admin' ? (
                    (() => {
                      const q = searchAll.trim().toLowerCase();
                      const list = [...users]
                        .filter(u => !q || `${u.firstname} ${u.lastname}`.toLowerCase().includes(q))
                        .sort((a, b) => `${a.lastname} ${a.firstname}`.localeCompare(`${b.lastname} ${b.firstname}`));
                      return (
                        <>
                          <div className="input-group mb-2">
                            <span className="input-group-text"><i className="bi bi-search"></i></span>
                            <input type="text" className="form-control" placeholder={t('avail.searchPlaceholder')}
                              value={searchAll} onChange={(e) => setSearchAll(e.target.value)} />
                          </div>
                          <div className="d-flex flex-column gap-1" style={{ maxHeight: 180, overflowY: 'auto' }}>
                            {list.length === 0 ? (
                              <span className="text-muted small">{t('avail.noMatch')}</span>
                            ) : list.map(u => (
                              <button key={u.id} type="button"
                                className={`btn btn-sm text-start ${filterUser === String(u.id) ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => setFilterUser(filterUser === String(u.id) ? '' : String(u.id))}>
                                {u.firstname} {u.lastname}
                              </button>
                            ))}
                          </div>
                        </>
                      );
                    })()
                  ) : (
                    <div className="form-control-plaintext">
                      <i className="bi bi-person-fill me-2"></i>
                      {user.firstname && user.lastname
                        ? `${user.firstname} ${user.lastname}`
                        : user.name || 'Benutzer'}
                      <small className="text-muted d-block">{t('avail.onlyYours')}</small>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {user.role === 'admin' && (
              <div className="col-md-6">
                <div className="card h-100">
                  <div className="card-body">
                    <h6 className="card-title d-flex align-items-center justify-content-between">
                      <span>{t('avail.withoutAvail')}</span>
                      <span className={`badge ${usersWithoutAvailability.length ? 'bg-danger' : 'bg-success'}`}>
                        {usersWithoutAvailability.length}
                      </span>
                    </h6>
                    {usersWithoutAvailability.length === 0 ? (
                      <p className="text-success mb-0"><i className="bi bi-check-circle me-1"></i>{t('avail.allHaveAvail')}</p>
                    ) : (() => {
                      const q = searchNoAvail.trim().toLowerCase();
                      const list = [...usersWithoutAvailability]
                        .filter(u => !q || `${u.firstname} ${u.lastname}`.toLowerCase().includes(q))
                        .sort((a, b) => `${a.lastname} ${a.firstname}`.localeCompare(`${b.lastname} ${b.firstname}`));
                      return (
                        <>
                          <div className="input-group mb-2">
                            <span className="input-group-text"><i className="bi bi-search"></i></span>
                            <input type="text" className="form-control" placeholder={t('avail.searchPlaceholder')}
                              value={searchNoAvail} onChange={(e) => setSearchNoAvail(e.target.value)} />
                          </div>
                          <div className="d-flex flex-wrap gap-2" style={{ maxHeight: 150, overflowY: 'auto' }}>
                            {list.length === 0 ? (
                              <span className="text-muted small">{t('avail.noMatch')}</span>
                            ) : list.map(u => (
                              <button key={u.id} type="button"
                                className={`btn btn-sm ${filterUser === String(u.id) ? 'btn-primary' : 'btn-outline-danger'}`}
                                title={t('avail.showWithout')}
                                onClick={() => setFilterUser(filterUser === String(u.id) ? '' : String(u.id))}>
                                {u.firstname} {u.lastname}
                              </button>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Partner-Bereich */}
          <PartnerSection user={user} t={t} expandedDate={expandedDate} dateRange={dateRange} startDate={startDate} endDate={endDate} />

          {/* Kalendar-Interface für Datumswahl */}
          <div className="card mb-4">
            <div className="card-body">
              <h6 className="card-title">{t('avail.dateSpecific')}</h6>

              {/* Zeitraum definieren – nur für Admins. Für Mitarbeiter ist der
                  Zeitraum fix (Wiesn, vorbelegt) → direkt der Kalender, ohne Dopplung. */}
              {user.role === 'admin' && (
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">{t('avail.fromDate')}</label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      if (e.target.value && endDate) {
                        generateDateRange(e.target.value, endDate);
                      }
                    }}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">{t('avail.toDate')}</label>
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      if (startDate && e.target.value) {
                        generateDateRange(startDate, e.target.value);
                      }
                    }}
                  />
                </div>
              </div>
              )}

              {/* Kalendar-Grid */}
              {startDate && endDate && (
                <div className="calendar-grid">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                      <h6 className="mb-0">📅 {t('avail.calendarView')}</h6>
                      <small className="text-muted">
                        <i className="bi bi-hand-index me-1"></i>
                        {t('avail.clickDate')}
                      </small>
                    </div>
                    {filterUser && (
                      <div className="text-end">
                        <small className="text-success">
                          <i className="bi bi-filter me-1"></i>
                          {t('avail.showingFor')} <strong>{users.find(u => u.id === parseInt(filterUser))?.firstname} {users.find(u => u.id === parseInt(filterUser))?.lastname}</strong>
                        </small>
                      </div>
                    )}
                  </div>
                  
                  {/* Hinweis */}
                  <div className="alert alert-light border py-2 mb-3">
                    <small>
                      <i className="bi bi-lightbulb me-1 text-warning"></i>
                      {t('avail.hintFillAll')}
                    </small>
                  </div>

                  {/* Legende */}
                  <div className="mb-3">
                    <small className="text-muted">
                      <span className="badge bg-success me-2">{t('avail.legendGreen')}</span> {t('avail.available')}
                      <span className="badge bg-danger me-2">{t('avail.legendRed')}</span> {t('avail.notAvailable')}
                      <span className="badge bg-warning me-2">{t('avail.legendYellow')}</span> {t('avail.mixed')}
                      <span className="badge bg-primary me-2 ms-2">{t('avail.legendBlue')}</span> {t('avail.selected')}
                      <span className="badge bg-outline-secondary ms-2">{t('avail.legendGray')}</span> {t('avail.noEntry')}
                    </small>
                  </div>

                  {/* Fortschrittsbalken */}
                  {(() => {
                    const uid = user.role === 'employee' ? user.id : (filterUser ? parseInt(filterUser) : null);
                    if (!uid || dateRange.length === 0) return null;
                    const filledDays = new Set(availabilities.filter(a => a.user_id === uid && a.id != null && dateRange.includes(a.date)).map(a => a.date)).size;
                    const totalDays = dateRange.length;
                    const pct = Math.round((filledDays / totalDays) * 100);
                    const availDays = new Set(availabilities.filter(a => a.user_id === uid && a.id != null && dateRange.includes(a.date) && (a.is_available === 1 || a.is_available === true)).map(a => a.date)).size;
                    const notAvailDays = new Set(availabilities.filter(a => a.user_id === uid && a.id != null && dateRange.includes(a.date) && (a.is_available === 0 || a.is_available === false)).map(a => a.date)).size;
                    const openDays = totalDays - filledDays;
                    return (
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <small className="fw-bold">{t('avail.progress')}</small>
                          <small className="text-muted">{filledDays}/{totalDays} {t('avail.days')}</small>
                        </div>
                        <div className="progress" style={{ height: '8px' }}>
                          <div className={`progress-bar ${pct === 100 ? 'bg-success' : pct >= 50 ? 'bg-primary' : 'bg-warning'}`} style={{ width: `${pct}%` }}></div>
                        </div>
                        <div className="d-flex gap-3 mt-1">
                          <small className="text-success">✅ {availDays} {t('avail.statsAvail')}</small>
                          <small className="text-danger">❌ {notAvailDays} {t('avail.statsNotAvail')}</small>
                          <small className="text-muted">⬜ {openDays} {t('avail.statsOpen')}</small>
                        </div>
                      </div>
                    );
                  })()}
                  <div className="row">
                    {calendarDates.map(date => {
                      // Use local date string to avoid timezone issues
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const dateStr = `${year}-${month}-${day}`;
                      
                      // Prüfe Verfügbarkeiten des ausgewählten Benutzers oder aller wenn kein Filter
                      const relevantAvailabilities = filterUser 
                        ? availabilities.filter(av => av.date === dateStr && av.user_id === parseInt(filterUser))
                        : availabilities.filter(av => av.date === dateStr);
                      
                      const hasAvailability = relevantAvailabilities.length > 0;
                      const isAvailable = relevantAvailabilities.some(av => av.is_available === true || av.is_available === 1 || av.is_available === "1");
                      const isNotAvailable = relevantAvailabilities.some(av => av.is_available === false || av.is_available === 0 || av.is_available === "0");
                      
                      // Bestimme die Kalender-Farbe
                      let buttonClass = 'btn-outline-secondary'; // Standard: Grau (keine Eingabe)
                      if (selectedDate === dateStr) {
                        buttonClass = 'btn-primary'; // Blau: ausgewählt
                      } else if (isAvailable && !isNotAvailable) {
                        buttonClass = 'btn-success'; // Grün: nur verfügbar
                      } else if (isNotAvailable && !isAvailable) {
                        buttonClass = 'btn-danger'; // Rot: nur nicht verfügbar
                      } else if (isAvailable && isNotAvailable) {
                        buttonClass = 'btn-warning'; // Gelb: gemischt (verfügbar UND nicht verfügbar am selben Tag)
                      }

                      // Debug-Info (nur bei Bedarf aktivieren)
                      
                      return (
                        <div key={dateStr} className="col-md-2 col-sm-3 col-4 mb-2">
                          <button
                            className={`btn w-100 ${buttonClass}`}
                            onClick={() => {
                              handleDateChange(dateStr);
                              if (expandedDate === dateStr) {
                                setExpandedDate(null);
                                setEditingAvailability(null);
                              } else {
                                setExpandedDate(dateStr);
                                setSliderStart(4);  // 07:00
                                setSliderEnd(40);   // 01:00
                                setFormData(prev => ({ ...prev, date: dateStr, is_available: true, notes: '' }));
                                setEditingAvailability(null);
                                setApplyDates([]);
                                setTimeout(() => {
                                  document.getElementById('inline-avail-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }, 100);
                              }
                            }}
                          >
                            <div className="text-center">
                              <div style={{fontSize: '0.8rem'}}>{date.toLocaleDateString('de-DE', { weekday: 'short' })}</div>
                              <div style={{fontSize: '1.1rem', fontWeight: 'bold'}}>{date.getDate()}</div>
                              <div style={{fontSize: '0.7rem'}}>{date.toLocaleDateString('de-DE', { month: 'short' })}</div>
                              {/* Bedarfs-Hinweis: 🔥 dringend · 🟡 einzelne Schichten · ✅ voll besetzt */}
                              {(() => {
                                const dt = demandText(dateStr);
                                if (!dt) return null;
                                return <div style={{ fontSize: '0.68rem', lineHeight: 1.2, marginTop: 2, opacity: 0.95 }}>{dt.icon} {dt.short}</div>;
                              })()}
                            </div>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Inline-Panel für Verfügbarkeit */}
                  {expandedDate && (
                    <div id="inline-avail-panel" className="card mt-3 border-primary shadow-sm">
                      <div className="card-body">
                        {/* Slider CSS */}
                        <style>{`
                          .time-slider {
                            -webkit-appearance: none;
                            appearance: none;
                            position: absolute;
                            width: 100%;
                            height: 6px;
                            background: transparent;
                            pointer-events: none;
                            outline: none;
                            top: 50%;
                            transform: translateY(-50%);
                            margin: 0;
                            padding: 0;
                          }
                          .time-slider::-webkit-slider-thumb {
                            -webkit-appearance: none;
                            appearance: none;
                            width: 28px;
                            height: 28px;
                            border-radius: 50%;
                            background: #0d6efd;
                            cursor: pointer;
                            pointer-events: auto;
                            border: 3px solid white;
                            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                            margin-top: -11px;
                          }
                          .time-slider::-moz-range-thumb {
                            width: 28px;
                            height: 28px;
                            border-radius: 50%;
                            background: #0d6efd;
                            cursor: pointer;
                            pointer-events: auto;
                            border: 3px solid white;
                            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                          }
                          .time-slider::-webkit-slider-runnable-track {
                            height: 6px;
                            background: transparent;
                          }
                          .time-slider::-moz-range-track {
                            height: 6px;
                            background: transparent;
                          }
                        `}</style>

                        {/* Header */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5 className="mb-0">
                            📅 {new Date(expandedDate + 'T00:00:00').toLocaleDateString('de-DE', { 
                              weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                            })}
                          </h5>
                          <button 
                            type="button" 
                            className="btn-close" 
                            onClick={() => { setExpandedDate(null); setEditingAvailability(null); setApplyDates([]); }}
                          />
                        </div>

                        {/* Bedarfs-Hinweis für diesen Tag */}
                        {(() => {
                          const dt = demandText(expandedDate);
                          if (!dt) return null;
                          const cls = dt.icon === '🔥' ? 'alert-danger' : dt.icon === '🟡' ? 'alert-warning' : 'alert-light border';
                          const parts = dt.long.split('**');
                          return (
                            <div className={`alert ${cls} py-2 mb-3`}>
                              <span className="me-1">{dt.icon}</span>
                              {parts.map((p, i) => (i % 2 ? <strong key={i}>{p}</strong> : <span key={i}>{p}</span>))}
                            </div>
                          );
                        })()}

                        {/* Existing entries for this date */}
                        {(() => {
                          const userId = user.role === 'employee' ? user.id : (filterUser ? parseInt(filterUser) : null);
                          const existing = userId 
                            ? availabilities.filter(a => a.date === expandedDate && a.user_id === userId && a.id != null)
                            : [];
                          if (existing.length > 0 && !editingAvailability) {
                            const newestId = Math.max(...existing.map((e) => e.id));
                            return (
                              <div className="mb-3">
                                <small className="text-muted fw-bold">{t('avail.existingEntries')}:</small>
                                {existing.map(entry => (
                                  <div key={entry.id} className="d-flex align-items-center justify-content-between bg-light rounded p-2 mt-1">
                                    <span>
                                      {(entry.is_available === 1 || entry.is_available === true) ? '✅' : '❌'}{' '}
                                      {entry.start_time} – {entry.end_time}
                                      {existing.length > 1 && entry.id === newestId && (
                                        <span className="badge bg-info ms-2">{t('avail.newestEntry')}</span>
                                      )}
                                      {entry.notes && <small className="text-muted ms-2">({entry.notes})</small>}
                                    </span>
                                    {isLocked(entry.date) ? (
                                      <span className="badge bg-secondary" title={lockedMsg}>🔒 eingeplant – Änderung über die Leitung</span>
                                    ) : (
                                    <div className="btn-group btn-group-sm">
                                      <button className="btn btn-outline-primary btn-sm" onClick={() => handleEdit(entry)}>
                                        <i className="bi bi-pencil"></i>
                                      </button>
                                      <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(entry.id)}>
                                        <i className="bi bi-trash"></i>
                                      </button>
                                    </div>
                                    )}
                                  </div>
                                ))}

                                {/* Ersetzen (Standard) oder bewusst zusätzliches Zeitfenster */}
                                <div className="border rounded p-2 mt-2">
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name="replaceMode"
                                      id="replaceModeYes"
                                      checked={replaceExisting}
                                      onChange={() => setReplaceExisting(true)}
                                    />
                                    <label className="form-check-label" htmlFor="replaceModeYes">
                                      <strong>{t('avail.replaceExisting')}</strong>{' '}
                                      <small className="text-muted">({t('avail.replaceExistingHint')})</small>
                                    </label>
                                  </div>
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name="replaceMode"
                                      id="replaceModeNo"
                                      checked={!replaceExisting}
                                      onChange={() => setReplaceExisting(false)}
                                    />
                                    <label className="form-check-label" htmlFor="replaceModeNo">
                                      <strong>{t('avail.addWindow')}</strong>{' '}
                                      <small className="text-muted">({t('avail.addWindowHint')})</small>
                                    </label>
                                  </div>
                                </div>
                                <hr />
                              </div>
                            );
                          }
                          return null;
                        })()}

                        {/* Admin without filter warning */}
                        {user.role === 'admin' && !filterUser && (
                          <div className="alert alert-warning py-2 mb-0">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {t('avail.adminFilterFirst')}
                          </div>
                        )}

                        {(user.role === 'employee' || filterUser) && (
                          <>
                            {/* Toggle Verfügbar / Nicht verfügbar */}
                            <div className="btn-group w-100 mb-3" role="group">
                              <button 
                                type="button"
                                className={`btn ${formData.is_available ? 'btn-success' : 'btn-outline-success'}`}
                                onClick={() => setFormData({...formData, is_available: true})}
                              >
                                <i className="bi bi-check-circle me-1"></i> {t('avail.available')}
                              </button>
                              <button 
                                type="button"
                                className={`btn ${!formData.is_available ? 'btn-danger' : 'btn-outline-danger'}`}
                                onClick={() => setFormData({...formData, is_available: false})}
                              >
                                <i className="bi bi-x-circle me-1"></i> {t('avail.notAvailable')}
                              </button>
                            </div>

                            {/* Time slider (only when available) */}
                            {formData.is_available && (
                              <div className="mb-3">
                                <div className="d-flex justify-content-between text-muted small mb-1">
                                  <span>🌅 05:00</span>
                                  <span>🌙 01:00</span>
                                </div>
                                
                                {/* Dual range slider */}
                                <div style={{ position: 'relative', height: '44px', padding: '0 2px' }}>
                                  {/* Track background */}
                                  <div style={{ 
                                    position: 'absolute', top: 'calc(50% - 3px)', left: '2px', right: '2px', 
                                    height: '6px', background: '#e9ecef', borderRadius: '3px' 
                                  }} />
                                  {/* Active track */}
                                  <div style={{ 
                                    position: 'absolute', top: 'calc(50% - 3px)', 
                                    left: `calc(2px + ${(sliderStart / 40) * 100}%)`,
                                    width: `${((sliderEnd - sliderStart) / 40) * 100}%`,
                                    height: '6px', background: '#0d6efd', borderRadius: '3px' 
                                  }} />
                                  <input 
                                    type="range" 
                                    className="time-slider" 
                                    min={0} max={40}
                                    value={sliderStart}
                                    onChange={e => setSliderStart(Math.min(Number(e.target.value), sliderEnd - 1))}
                                  />
                                  <input 
                                    type="range" 
                                    className="time-slider" 
                                    min={0} max={40}
                                    value={sliderEnd}
                                    onChange={e => setSliderEnd(Math.max(Number(e.target.value), sliderStart + 1))}
                                  />
                                </div>

                                {/* Time display */}
                                <div className="text-center mt-2">
                                  <span className="badge bg-primary fs-6 px-3 py-2">
                                    {TIME_SLOTS[sliderStart]} – {TIME_SLOTS[sliderEnd]}
                                  </span>
                                  {(sliderStart === 0 || sliderStart === 4) && sliderEnd === 40 && (
                                    <div className="text-success small mt-1">
                                      <i className="bi bi-check-circle me-1"></i>{t('avail.fullDayFlex')}
                                    </div>
                                  )}
                                </div>

                                {/* Short availability warning */}
                                {(sliderEnd - sliderStart) <= 6 && (sliderEnd - sliderStart) > 0 && (
                                  <div className="alert alert-warning mt-3 py-2 small">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    {t('avail.shortAvailWarning')}
                                  </div>
                                )}

                                {/* Info hint */}
                                <div className="alert alert-info mt-3 py-2 small mb-0">
                                  <i className="bi bi-info-circle me-2"></i>
                                  {t('avail.hintOnlyAvail')}
                                </div>
                              </div>
                            )}

                            {/* Notes */}
                            <div className="mb-3">
                              <label className="form-label small text-muted">
                                <i className="bi bi-pencil me-1"></i>{t('avail.noteOptional')}
                              </label>
                              <textarea 
                                className="form-control" 
                                rows="2"
                                value={formData.notes}
                                onChange={e => setFormData({...formData, notes: e.target.value})}
                                placeholder={t('avail.notePlaceholder')}
                              />
                            </div>

                            {/* Apply to other days */}
                            {!editingAvailability && dateRange.length > 1 && (
                              <div className="mb-3">
                                <label className="form-label small text-muted">
                                  <i className="bi bi-calendar-plus me-1"></i>{t('avail.alsoApplyTo')}
                                </label>
                                <div className="mb-2">
                                  <button type="button" className="btn btn-sm btn-outline-primary me-1 mb-1"
                                    onClick={() => {
                                      const weekdays = dateRange.filter(d => {
                                        const dow = new Date(d + 'T00:00:00').getDay();
                                        return dow >= 1 && dow <= 5 && d !== expandedDate;
                                      });
                                      const allSelected = weekdays.length > 0 && weekdays.every(d => applyDates.includes(d));
                                      setApplyDates(allSelected 
                                        ? applyDates.filter(d => !weekdays.includes(d)) 
                                        : [...new Set([...applyDates, ...weekdays])]);
                                    }}
                                  >Mo–Fr</button>
                                  <button type="button" className="btn btn-sm btn-outline-info me-1 mb-1"
                                    onClick={() => {
                                      const weekends = dateRange.filter(d => {
                                        const dow = new Date(d + 'T00:00:00').getDay();
                                        return (dow === 0 || dow === 6) && d !== expandedDate;
                                      });
                                      const allSelected = weekends.length > 0 && weekends.every(d => applyDates.includes(d));
                                      setApplyDates(allSelected 
                                        ? applyDates.filter(d => !weekends.includes(d)) 
                                        : [...new Set([...applyDates, ...weekends])]);
                                    }}
                                  >Sa–So</button>
                                  <button type="button" className="btn btn-sm btn-outline-secondary me-1 mb-1"
                                    onClick={() => {
                                      const allOther = dateRange.filter(d => d !== expandedDate);
                                      const allSelected = allOther.length > 0 && allOther.every(d => applyDates.includes(d));
                                      setApplyDates(allSelected ? [] : allOther);
                                    }}
                                  >{t('avail.allDays')}</button>
                                  {applyDates.length > 0 && (
                                    <button type="button" className="btn btn-sm btn-outline-secondary mb-1"
                                      onClick={() => setApplyDates([])}
                                    >
                                      <i className="bi bi-x me-1"></i>{t('avail.deselect')}
                                    </button>
                                  )}
                                </div>
                                {(() => {
                                  // Tage mit vorhandenen Einträgen markieren + Ersetzen-Warnung,
                                  // damit "Mo–Fr doppelt anlegen" nicht unbemerkt passiert
                                  const uid = user.role === 'employee' ? user.id : (filterUser ? parseInt(filterUser) : null);
                                  const hasEntries = (d) => uid != null
                                    && availabilities.some(a => a.id != null && a.user_id === uid && a.date === d);
                                  const selectedWithEntries = applyDates.filter(hasEntries);
                                  return (
                                    <>
                                      <div className="d-flex flex-wrap gap-1">
                                        {dateRange.filter(d => d !== expandedDate).map(d => {
                                          const dateObj = new Date(d + 'T00:00:00');
                                          const isSelected = applyDates.includes(d);
                                          const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
                                          return (
                                            <button
                                              key={d}
                                              type="button"
                                              className={`btn btn-sm ${isSelected ? 'btn-primary' : isWeekend ? 'btn-outline-info' : 'btn-outline-secondary'}`}
                                              onClick={() => setApplyDates(isSelected
                                                ? applyDates.filter(x => x !== d)
                                                : [...applyDates, d])}
                                            >
                                              {hasEntries(d) && <span title={t('avail.existingEntries')}>● </span>}
                                              {dateObj.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' })}
                                            </button>
                                          );
                                        })}
                                      </div>
                                      {applyDates.length > 0 && (
                                        <small className="text-success mt-1 d-block">
                                          <i className="bi bi-check-circle me-1"></i>
                                          {t('avail.additionalDays', { count: applyDates.length })}
                                        </small>
                                      )}
                                      {selectedWithEntries.length > 0 && (
                                        <div className="alert alert-warning py-1 px-2 small mt-1 mb-0">
                                          ⚠️ {t('avail.daysHaveEntries', { count: selectedWithEntries.length })} – {' '}
                                          <strong>{replaceExisting ? t('avail.willReplace') : t('avail.willAdd')}</strong>
                                          <button
                                            type="button"
                                            className="btn btn-link btn-sm p-0 ms-2 align-baseline"
                                            onClick={() => setReplaceExisting(!replaceExisting)}
                                          >
                                            ({t('avail.switchMode')})
                                          </button>
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            )}

                            {/* Save button */}
                            <button 
                              type="button" 
                              className="btn btn-primary w-100 btn-lg"
                              onClick={handleInlineSubmit}
                            >
                              <i className="bi bi-check-lg me-2"></i>
                              {editingAvailability 
                                ? t('avail.update') 
                                : `${t('avail.save')}${applyDates.length > 0 ? ` (${1 + applyDates.length} ${t('avail.days')})` : ''}`}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Verfügbarkeiten-Übersicht */}
          <div className="card">
            <div className="card-body">
              {loading ? (
                <div className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Lade...</span>
                  </div>
                </div>
              ) : (
                <div>
                  {Object.keys(groupedAvailabilities).length === 0 ? (
                    <div className="text-center py-4">
                      <i className="bi bi-calendar-x display-1 text-muted"></i>
                      <p className="text-muted mt-2">{t('avail.noData')}</p>
                    </div>
                  ) : (
                    Object.entries(groupedAvailabilities).map(([userName, userAvailabilities]) => {
                      const userId = userAvailabilities[0]?.user_id;
                      return (
                      <div key={userName} className="mb-4">
                        <h5 className="border-bottom pb-2">
                          <div className="d-flex align-items-center">
                            <div className={`me-2 rounded-circle ${
                              getUserStatusColor(userId) === 'success' ? 'bg-success' : 
                              getUserStatusColor(userId) === 'warning' ? 'bg-warning' : 
                              'bg-danger'
                            }`} style={{width: '12px', height: '12px'}}></div>
                            <i className="bi bi-person-circle me-2"></i>{userName}
                          </div>
                        </h5>
                        <div className="table-responsive">
                          <table className="table table-sm table-hover">
                            <thead className="table-light">
                              <tr>
                                <th>{t('avail.weekday')}</th>
                                <th>Zeit</th>
                                <th>Status</th>
                                <th>{t('avail.notes')}</th>
                                <th>{t('avail.actions')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {userAvailabilities
                                .sort((a, b) => {
                                  // Mitarbeiter ohne Verfügbarkeitseinträge nach oben
                                  if (!a.date && b.date) return -1;
                                  if (a.date && !b.date) return 1;
                                  if (!a.date && !b.date) return a.lastname.localeCompare(b.lastname);
                                  return new Date(a.date + 'T00:00:00') - new Date(b.date + 'T00:00:00');
                                })
                                .map((availability, index) => (
                                <tr key={availability.id || `user-${availability.user_id}-${index}`}>
                                  <td>
                                    {availability.date ? (
                                      <span className="badge bg-secondary">
                                        {new Date(availability.date + 'T00:00:00').toLocaleDateString('de-DE', {
                                          weekday: 'short',
                                          day: '2-digit',
                                          month: '2-digit'
                                        })}
                                      </span>
                                    ) : (
                                      <span className="badge bg-warning text-dark">
                                        <i className="bi bi-exclamation-triangle me-1"></i>Keine Verfügbarkeit
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    {availability.date ? (
                                      <>
                                        {getAvailabilityTypeDisplay(availability)}
                                        <small className="text-muted">
                                          {availability.start_time} - {availability.end_time}
                                          {availability.end_time === '01:00' && <span className="text-info"> (+1 Tag)</span>}
                                        </small>
                                        {(() => {
                                          // Mehrfach-Einträge am selben Tag sichtbar machen: der
                                          // jüngste (höchste ID) ist mutmaßlich der gewollte Stand
                                          const sameDay = userAvailabilities.filter(
                                            (a) => a.id != null && a.user_id === availability.user_id && a.date === availability.date
                                          );
                                          if (sameDay.length < 2) return null;
                                          const isNewest = availability.id === Math.max(...sameDay.map((a) => a.id));
                                          return (
                                            <span className={`badge ms-2 ${isNewest ? 'bg-info' : 'bg-warning text-dark'}`}>
                                              {isNewest ? t('avail.newestEntry') : t('avail.olderEntry')}
                                            </span>
                                          );
                                        })()}
                                      </>
                                    ) : (
                                      <span className="text-muted">
                                        <i className="bi bi-clock me-1"></i>Noch nicht eingetragen
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    {availability.date ? (
                                      availability.is_available ? (
                                        <span className="badge bg-success">
                                          <i className="bi bi-check-circle me-1"></i>{t('avail.available')}
                                        </span>
                                      ) : (
                                        <span className="badge bg-danger">
                                          <i className="bi bi-x-circle me-1"></i>{t('avail.notAvailable')}
                                        </span>
                                      )
                                    ) : (
                                      <span className="badge bg-secondary">
                                        <i className="bi bi-question-circle me-1"></i>Ausstehend
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    <small>{availability.notes || (availability.date ? '' : t('avail.pleaseEnter'))}</small>
                                  </td>
                                  <td>
                                    <div className="btn-group btn-group-sm">
                                      {availability.date && isLocked(availability.date) ? (
                                        <span className="badge bg-secondary" title={lockedMsg}>🔒 eingeplant</span>
                                      ) : availability.date ? (
                                        <>
                                          <button
                                            className="btn btn-outline-primary"
                                            onClick={() => handleEdit(availability)}
                                          >
                                            <i className="bi bi-pencil"></i>
                                          </button>
                                          <button
                                            className="btn btn-outline-danger"
                                            onClick={() => handleDelete(availability.id)}
                                          >
                                            <i className="bi bi-trash"></i>
                                          </button>
                                        </>
                                      ) : (
                                        <button
                                          className="btn btn-outline-success"
                                          onClick={() => {
                                            if (user.role === 'admin') {
                                              setFilterUser(availability.user_id.toString());
                                            }
                                            setExpandedDate(dateRange[0] || null);
                                            setSliderStart(4);  // 07:00
                                            setSliderEnd(40);   // 01:00
                                            setFormData(prev => ({ ...prev, is_available: true, notes: '' }));
                                            setEditingAvailability(null);
                                            setApplyDates([]);
                                            setTimeout(() => {
                                              document.querySelector('.calendar-grid')?.scrollIntoView({ behavior: 'smooth' });
                                            }, 100);
                                          }}
                                          title={t('avail.add')}
                                        >
                                          <i className="bi bi-plus-circle"></i>
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )})
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default UserAvailability;
